import { useEffect, useRef, useState } from 'react';
import api from '../../../services/api';
import { DUREE_LABELS } from '../shareCodesHelpers';
import { Users, Check, X, Copy, Send, Mail, Loader2 } from 'lucide-react';

// Liste des codes de partage thématique (accès complet, compte requis).
// Autonome : copie, édition du groupe et invitation email gérées ici (onReload pour rafraîchir).
export default function ThematiqueCodesList({ codes, userStats, onReload }) {
    const [copied, setCopied] = useState(null);
    const [editLabelId, setEditLabelId] = useState(null);
    const [editLabelValue, setEditLabelValue] = useState('');
    const [inviteCodeId, setInviteCodeId] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteSending, setInviteSending] = useState(false);
    const [inviteMsg, setInviteMsg] = useState(null);
    const inviteEmailRef = useRef(null);
    // Au clic sur « Envoyer par email » : scroller vers l'input et lui donner le focus.
    useEffect(() => {
        if (inviteCodeId && inviteEmailRef.current) {
            inviteEmailRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            inviteEmailRef.current.focus({ preventScroll: true });
        }
    }, [inviteCodeId]);

    const handleCopy = (code) => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(code);
            setTimeout(() => setCopied(null), 2000);
        });
    };

    const handleUpdateLabel = async (codeId) => {
        try {
            await api.post('/share/codes.php', { action: 'update_label', code_id: codeId, label: editLabelValue });
            setEditLabelId(null);
            onReload();
        } catch {}
    };

    const handleInvite = async (codeId) => {
        if (!inviteEmail || !inviteEmail.includes('@')) return;
        setInviteSending(true);
        setInviteMsg(null);
        try {
            const { data } = await api.post('/share/invite.php', { code_id: codeId, email: inviteEmail });
            if (data.success) {
                setInviteMsg({ type: 'success', text: data.data.message });
                setInviteCodeId(null);
                setInviteEmail('');
                onReload(); // refresh to show email_invitation
            }
        } catch (err) {
            setInviteMsg({ type: 'error', text: err.response?.data?.message || 'Erreur' });
        }
        setInviteSending(false);
    };

    const statsByEmail = new Map((userStats || []).map(u => [(u.email || '').toLowerCase(), u]));

    return (
        <>
        <h4 style={{ marginBottom: 10, fontSize: '0.9em', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--heading)' }}>
            <Users size={15} /> Codes de partage th&eacute;matique (groupe) ({codes.length})
        </h4>
        {codes.length === 0 ? (
            <p style={{ fontSize: '0.85em', color: 'var(--text-muted)', fontStyle: 'italic', margin: '0 0 16px' }}>Aucun code existant.</p>
        ) : (<>
            <div style={{ overflowX: 'auto' }}>
                <table className="share-panel__table">
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Groupe</th>
                            <th>Dur&eacute;e</th>
                            <th>Statut</th>
                            <th>Utilis&eacute; par</th>
                            <th style={{ textAlign: 'right' }}>Quiz</th>
                            <th style={{ textAlign: 'right' }}>R&eacute;ussite</th>
                            <th>Derni&egrave;re activit&eacute;</th>
                            <th>Dates</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {codes.map(c => {
                            const stat = c.status === 'REDEEMED' && c.redeemed_by_email
                                ? statsByEmail.get(c.redeemed_by_email.toLowerCase())
                                : null;
                            const rateColor = stat && stat.success_rate !== null
                                ? (stat.success_rate >= 70 ? '#22c55e' : stat.success_rate >= 50 ? '#f59e0b' : '#ef4444')
                                : 'var(--text-muted)';
                            return (
                            <tr key={c.id} className={c.status !== 'AVAILABLE' ? 'share-panel__row--muted' : ''}>
                                <td><code style={{ fontFamily: 'monospace', fontSize: '0.82em', letterSpacing: 1 }}>{c.code}</code></td>
                                <td>
                                    {editLabelId === c.id ? (
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                            <input type="text" value={editLabelValue} onChange={e => setEditLabelValue(e.target.value)}
                                                placeholder="Groupe..."
                                                onKeyDown={e => { if (e.key === 'Enter') handleUpdateLabel(c.id); if (e.key === 'Escape') setEditLabelId(null); }}
                                                style={{ width: 110, padding: '3px 6px', fontSize: '0.82em', borderRadius: 4, border: '1px solid var(--border)' }}
                                                autoFocus />
                                            <button className="share-panel__code-copy" onClick={() => handleUpdateLabel(c.id)}><Check size={12} /></button>
                                            <button className="share-panel__code-copy" onClick={() => setEditLabelId(null)}><X size={12} /></button>
                                        </div>
                                    ) : (
                                        <span style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                            onClick={() => { setEditLabelId(c.id); setEditLabelValue(c.label || ''); }}>
                                            {c.label
                                                ? <span style={{ fontSize: '0.82em', padding: '2px 8px', borderRadius: 4, background: 'rgba(124,58,237,0.1)', color: 'var(--primary)', fontWeight: 500 }}>{c.label}</span>
                                                : <span style={{ fontSize: '0.78em', opacity: 0.4, whiteSpace: 'nowrap' }}>+ groupe</span>
                                            }
                                        </span>
                                    )}
                                </td>
                                <td style={{ fontSize: '0.82em', color: 'var(--text-muted)' }}>{DUREE_LABELS[c.duree]}</td>
                                <td>
                                    <span className={`share-panel__code-badge share-panel__code-badge--${c.status.toLowerCase()}`}>
                                        {c.status === 'AVAILABLE' ? 'Disponible' : c.status === 'REDEEMED' ? 'Utilisé' : 'Expiré'}
                                    </span>
                                </td>
                                <td style={{ fontSize: '0.82em' }}>
                                    {c.status === 'REDEEMED'
                                        ? <span>{c.redeemed_by_name && <>{c.redeemed_by_name}<br /></>}{c.redeemed_by_email || '—'}</span>
                                        : c.email_invitation
                                            ? <span style={{ color: 'var(--text-muted)' }} title="Invitation envoyée"><Mail size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />{c.email_invitation.split('@')[0]}</span>
                                            : <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                                    }
                                </td>
                                <td style={{ fontSize: '0.82em', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                    {stat ? (
                                        <span><strong>{stat.quiz_completed}</strong>{stat.quiz_in_progress > 0 && <span style={{ color: 'var(--text-muted)' }}> +{stat.quiz_in_progress}</span>}</span>
                                    ) : <span style={{ opacity: 0.3 }}>&mdash;</span>}
                                </td>
                                <td style={{ fontSize: '0.82em', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                    {stat && stat.success_rate !== null
                                        ? <span style={{ fontWeight: 600, color: rateColor }}>{stat.success_rate}%</span>
                                        : <span style={{ opacity: 0.3 }}>&mdash;</span>}
                                </td>
                                <td style={{ fontSize: '0.78em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                    {stat && stat.last_activity
                                        ? new Date(stat.last_activity).toLocaleDateString('fr-FR')
                                        : <span style={{ opacity: 0.3 }}>&mdash;</span>}
                                </td>
                                <td style={{ fontSize: '0.78em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                    {c.status === 'REDEEMED' && c.redeemed_at && c.expires_at
                                        ? <>{new Date(c.redeemed_at).toLocaleDateString('fr-FR')} &rarr; {new Date(c.expires_at + 'T00:00:00').toLocaleDateString('fr-FR')}</>
                                        : <span>&mdash;</span>
                                    }
                                </td>
                                <td>
                                    {c.status === 'AVAILABLE' && (
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                            <button className="share-panel__code-copy" onClick={() => handleCopy(c.code)} title="Copier">
                                                {copied === c.code ? <Check size={13} /> : <Copy size={13} />}
                                            </button>
                                            <button className="share-panel__code-copy"
                                                onClick={() => { setInviteCodeId(inviteCodeId === c.id ? null : c.id); setInviteEmail(c.email_invitation || ''); setInviteMsg(null); }}
                                                title="Envoyer par email">
                                                <Send size={13} />
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {/* Invite form below table */}
            {inviteCodeId && (
                <div className="share-panel__invite-form" style={{ marginTop: 8 }}>
                    <span style={{ fontSize: '0.82em', color: 'var(--text-muted)' }}>Inviter :</span>
                    <input type="email" placeholder="email@exemple.com"
                        ref={inviteEmailRef}
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleInvite(inviteCodeId)} />
                    <button className="btn btn-xs btn-primary"
                        onClick={() => handleInvite(inviteCodeId)}
                        disabled={inviteSending || !inviteEmail.includes('@')}>
                        {inviteSending ? <Loader2 size={12} className="spin" /> : <><Send size={12} /> Envoyer</>}
                    </button>
                </div>
            )}
            {inviteMsg && (
                <div className={`share-panel__invite-msg share-panel__invite-msg--${inviteMsg.type}`}>
                    {inviteMsg.text}
                </div>
            )}
        </>)}
        </>
    );
}
